import { computed, defineComponent, Fragment, h, ref, watchEffect } from 'vue';

import { classNames, parseStyleText } from '@components/utils';

import { useInjectBody, useInjectTable } from '../../context';
import { getColumnsKey } from '../../utils';
import { Cell } from '../Cell';

import ExpandedRow from './ExpandedRow';

import type { NTable } from '../../interface';

interface IBodyRowProps<RecordType = NTable.TDefaultRecord> {
  record: RecordType;
  index: number;
  renderIndex: number;
  recordKey: number | string;
  expandedKeys: Set<NTable.TKey>;
  rowComponent: NTable.NCustomize.TCustomizeComponent;
  cellComponent: NTable.NCustomize.TCustomizeComponent;
  customRow?: NTable.NCustomize.TGetComponentProps<RecordType>;
  rowExpandable: NTable.NExpand.IExpandable['rowExpandable'];
  indent?: number;
  rowKey: NTable.TKey;
  getRowKey: NTable.NRow.TGetRowKey<RecordType>;
  childrenColumnName: string;
}

export default defineComponent<IBodyRowProps>({
  name: 'BodyRow',
  inheritAttrs: false,
  props: [
    'record',
    'index',
    'renderIndex',
    'recordKey',
    'expandedKeys',
    'rowComponent',
    'cellComponent',
    'customRow',
    'rowExpandable',
    'indent',
    'rowKey',
    'getRowKey',
    'childrenColumnName'
  ] as any,
  setup(props, { attrs }) {
    const tableContext = useInjectTable();
    const bodyContext = useInjectBody();
    const expandRendered = ref(false);

    const expanded = computed(() => props.expandedKeys && props.expandedKeys.has(props.recordKey));

    watchEffect(() => {
      if (expanded.value) {
        expandRendered.value = true;
      }
    });

    const rowSupportExpand = computed(
      () => bodyContext.expandableType === 'row' && (!props.rowExpandable || props.rowExpandable(props.record))
    );
    const nestExpandable = computed(() => bodyContext.expandableType === 'nest');
    const hasNestChildren = computed(
      () =>
        props.childrenColumnName && props.record && (props.record as NTable.TDefaultRecord)[props.childrenColumnName]
    );
    const mergedExpandable = computed(() => rowSupportExpand.value || nestExpandable.value);

    const onInternalTriggerExpand = (record: NTable.TDefaultRecord, event: MouseEvent) => {
      bodyContext.onTriggerExpand(record, event);
    };

    // =========================== Row ===========================
    const additionalProps = computed<Record<string, any>>(() => props.customRow?.(props.record, props.index) || {});

    const onClick: (event: MouseEvent) => void = (event, ...args) => {
      if (bodyContext.expandRowByClick && mergedExpandable.value) {
        onInternalTriggerExpand(props.record, event);
      }

      additionalProps.value?.onClick?.(event, ...args);
    };

    const computedRowClassName = computed(() => {
      const { record, index, indent } = props;
      const { rowClassName } = bodyContext;

      if (typeof rowClassName === 'string') {
        return rowClassName;
      } else if (typeof rowClassName === 'function') {
        return rowClassName(record, index, indent as number);
      }

      return '';
    });

    const columnsKey = computed(() => getColumnsKey(bodyContext.flattenColumns));

    return () => {
      const { class: className, style } = attrs as any;
      const { record, index, rowKey, indent = 0, rowComponent: RowComponent, cellComponent } = props;
      const { fixedInfoList, transformCellText } = tableContext;
      const { flattenColumns, expandedRowClassName, indentSize, expandIcon, expandedRowRender, expandIconColumnIndex } =
        bodyContext;

      const baseRowNode = h(
        RowComponent,
        {
          ...additionalProps.value,
          ['data-row-key']: rowKey,
          class: classNames(
            className,
            computedRowClassName.value,
            additionalProps.value.class,
            'cds-table__row',
            `cds-table__row--level-${indent}`
          ),
          style: {
            ...style,
            ...parseStyleText(additionalProps.value.style)
          },
          onClick
        },
        flattenColumns.map((column, colIndex) => {
          const { customRender, dataIndex, class: columnClassName } = column;
          const key = columnsKey.value[colIndex];
          const fixedInfo = fixedInfoList[colIndex];

          let additionalCellProps;

          if (column.customCell) {
            additionalCellProps = column.customCell(record, index, column);
          }

          const appendNode =
            colIndex === (expandIconColumnIndex || 0) && nestExpandable.value
              ? h(Fragment, [
                  h('span', {
                    style: { paddingLeft: `${indentSize * indent}px` },
                    class: classNames('cds-table__row-indent', `cds-table__row-indent-level--${indent}`)
                  }),
                  expandIcon({
                    expanded: expanded.value,
                    expandable: hasNestChildren.value,
                    record,
                    isTree: true,
                    onExpand: onInternalTriggerExpand
                  })
                ])
              : null;

          return h(Cell, {
            cellType: 'body',
            key,
            record,
            index,
            dataIndex,
            column,
            renderIndex: props.renderIndex,
            ellipsis: column.ellipsis,
            align: column.align,
            component: cellComponent,
            class: columnClassName,
            customRender,
            ...fixedInfo,
            additionalProps: additionalCellProps,
            transformCellText,
            appendNode
          });
        })
      );
      // =========================== Row ===========================

      // ======================== Expand Row =========================
      let expandRowNode;
      if (rowSupportExpand.value && (expandRendered.value || expanded.value)) {
        const expandContent =
          expandedRowRender && expandedRowRender({ record, index, indent: indent + 1, expanded: expanded.value });
        const computedExpandedRowClassName = expandedRowClassName && expandedRowClassName(record, index, indent);
        expandRowNode = h(
          ExpandedRow,
          {
            class: classNames(
              'cds-table__expanded-row',
              `cds-table__expanded-row-level--${indent + 1}`,
              computedExpandedRowClassName
            ),
            expanded: expanded.value,
            component: RowComponent,
            cellComponent,
            colSpan: flattenColumns.length,
            isEmpty: false
          },
          {
            default: () => expandContent
          }
        );
      }

      return h(Fragment, [baseRowNode, expandRowNode]);
    };
  }
});
